import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import Icon from '../AppIcon';

/**
 * Reusable confirm dialog component.
 * Usage: <ConfirmDialog isOpen={...} onConfirm={...} onCancel={...} title="..." message="..." variant="danger|warning|info" />
 */
const ConfirmDialog = ({
  isOpen,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger', // danger | warning | info
  onConfirm,
  onCancel,
}) => {
  const iconMap = {
    danger: { name: 'Trash2', bg: 'bg-destructive/10', color: 'text-destructive' },
    warning: { name: 'AlertTriangle', bg: 'bg-warning/10', color: 'text-warning' },
    info: { name: 'Info', bg: 'bg-primary/10', color: 'text-primary' },
  };

  const confirmVariantMap = {
    danger: 'destructive',
    warning: 'default',
    info: 'default',
  };

  const { name, bg, color } = iconMap[variant] || iconMap.danger;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
          />

          {/* Dialog */}
          <motion.div
            className="relative bg-card border border-border rounded-xl shadow-xl w-full max-w-md p-6"
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full ${bg} flex items-center justify-center`}>
                <Icon name={name} size={20} className={color} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-base text-foreground mb-1">{title}</h3>
                {message && (
                  <p className="font-body text-sm text-muted-foreground">{message}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" size="sm" onClick={onCancel}>
                {cancelLabel}
              </Button>
              <Button variant={confirmVariantMap[variant]} size="sm" onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
